import {
  CustomsFormItem,
  CustomsItemDecision,
  DropOffPointResponse,
  GetShipmentCustomsDecisionsRequest,
  GetShipmentCustomsDecisionsResponse,
  GetShipmentCustomsItemsRequest,
  GetShipmentCustomsItemsResponse,
  ReceptionStatus,
  ShipmentAttachmentListItem,
  ShipmentCustomsItem,
  ShipmentDetailItem,
  ShipmentDetailsResponse,
  ShipmentItemListResponse,
  ShipmentListItem,
  ShipmentMovementScheduleResponse,
  ShipmentReceptionAttachment,
  ShipmentReceptionSummary,
  ShipmentSupplierDetailsResponse,
  SubmitShipmentCustomsDecisionRequest,
  UpdateShipmentDetailsByIdRequest,
  UpdateShipmentDetailsByIdResponse,
  UpdateShipmentSupplierByIdRequest
} from './types';
import { PaginatedResponse, whitelineApi } from '@/shared/api';
import { ShipmentPipelineStatus } from '../model/shipmentPipeline';

export type {
  CustomsFormItem,
  CustomsItemDecision,
  CustomsRegime,
  CustomsValueMethod,
  DeliveryDestinationType,
  DisplayCurrencyMode,
  DropOffPointCoordinates,
  DropOffPointDimensions,
  DropOffPointMeasure,
  DropOffPointProvince,
  DropOffPointResponse,
  DropOffPointWorkingHours,
  EstimateMode,
  FileSourceSystem,
  GetShipmentCustomsDecisionsRequest,
  GetShipmentCustomsDecisionsResponse,
  GetShipmentCustomsItemsRequest,
  GetShipmentCustomsItemsResponse,
  GtinAcquisitionMode,
  HandoverCurrency,
  HsSource,
  IncotermsCode,
  ItemStatus,
  MarkingCategory,
  MarkingExecutionSite,
  MarkingStatusOnAccept,
  MeasurementStatus,
  OriginalCurrency,
  PaymentMilestoneCategory,
  PaymentMilestoneRelativeTo,
  PermitReviewStatus,
  PriceGuardStatus,
  ProductMasterMatchStatus,
  ProductMasterSourceSystem,
  ServiceCode,
  ServicePercentBase,
  ServicePricingModel,
  ServiceVatMode,
  ReceptionClientDecisionStatus,
  ReceptionDecisionBucket,
  ReceptionDecisionsSummary,
  ReceptionStatus,
  ShipmentAttachmentListItem,
  ShipmentAttachmentGroup,
  ShipmentCustomsItem,
  ShipmentCustomsStatus,
  ShipmentCustomsSummary,
  ShipmentDetailDeliveryDestination,
  ShipmentDetailItem,
  ShipmentDetailPaymentMilestone,
  ShipmentDetailService,
  ShipmentDetailsResponse,
  ShipmentErrorResponse,
  ShipmentGoodsCostCurrency,
  ShipmentGoodsPaymentTerms,
  ShipmentItemListResponse,
  ShipmentItemPhoto,
  ShipmentItemPhotoSource,
  ShipmentListItem,
  ShipmentMovementScheduleResponse,
  ShipmentMovementScheduleStage,
  ShipmentPaymentStage,
  ShipmentPaymentStageType,
  ShipmentPermitReviewStatus,
  ShipmentReceptionAttachment,
  ShipmentReceptionPositionType,
  ShipmentReceptionSummary,
  ShipmentSupplierDetailsResponse,
  ShipmentSupplierExportMode,
  ShipmentStatus,
  SubmitShipmentCustomsDecisionRequest,
  TransportMode,
  UpdateShipmentDetailsByIdRequest,
  UpdateShipmentDetailsByIdResponse,
  UpdateShipmentSupplierByIdRequest
} from './types';

export interface GetDropOffPointsRequest {
  height?: number;
  length?: number;
  shipment_id?: string;
  volume?: number;
  weight?: number;
  width?: number;
}

export type GetDropOffPointsResponse = DropOffPointResponse[];

export const getDropOffPoints = async (
  payload: GetDropOffPointsRequest = {}
): Promise<GetDropOffPointsResponse> => {
  const response = await whitelineApi.get<GetDropOffPointsResponse>('/api/drop-off-points', {
    params: payload
  });

  return response.data;
};

export type GetShipmentsFilter =
  | {
      name: 'invoice_number';
      value: string;
    }
  | {
      name: 'status';
      value: ShipmentPipelineStatus[];
    };

export interface GetShipmentsRequest {
  accepted_from?: string;
  accepted_to?: string;
  filters?: GetShipmentsFilter[];
  org_id?: string;
  page?: number;
  page_size?: number;
}

export type GetShipmentsResponse = PaginatedResponse<ShipmentListItem>;

export interface GetShipmentCountsRequest {
  org_id: string;
}

export interface GetShipmentCountsResponse {
  active_count: number;
  completed_count: number;
  customs_count: number;
  in_transit_count: number;
  warehouse_count: number;
}

export const getShipmentCounts = async (
  payload: GetShipmentCountsRequest
): Promise<GetShipmentCountsResponse> => {
  const response = await whitelineApi.get<GetShipmentCountsResponse>('/api/shipments/counts', {
    params: payload
  });

  return response.data;
};

export const getShipments = async (
  payload: GetShipmentsRequest = {}
): Promise<GetShipmentsResponse> => {
  const response = await whitelineApi.post<GetShipmentsResponse>('/api/shipments', payload);

  return response.data;
};

export interface GetShipmentByIdRequest {
  id: string;
}

export const getShipmentById = async (
  payload: GetShipmentByIdRequest
): Promise<ShipmentDetailsResponse> => {
  const response = await whitelineApi.get<ShipmentDetailsResponse>(`/api/shipments/${payload.id}`);

  return response.data;
};

export type ShipmentPaymentQrAmount = string;

export interface GetShipmentPaymentQrRequest {
  amount?: ShipmentPaymentQrAmount;
  itemId?: string;
  shipmentId: string;
}

export type GetShipmentPaymentQrResponse = Blob;

export const getShipmentPaymentQr = async (
  payload: GetShipmentPaymentQrRequest
): Promise<GetShipmentPaymentQrResponse> => {
  const response = await whitelineApi.get<GetShipmentPaymentQrResponse>(
    `/api/shipments/${payload.shipmentId}/payment-qr`,
    {
      ...(payload.amount || payload.itemId ? {
        params: {
          ...(payload.amount ? { amount: payload.amount } : {}),
          ...(payload.itemId ? { itemId: payload.itemId } : {})
        }
      } : {}),
      responseType: 'blob'
    }
  );

  return response.data;
};

export interface GetShipmentMovementScheduleByIdRequest {
  id: string;
}

export const getShipmentMovementScheduleById = async (
  payload: GetShipmentMovementScheduleByIdRequest
): Promise<ShipmentMovementScheduleResponse> => {
  const response = await whitelineApi.get<ShipmentMovementScheduleResponse>(
    `/api/shipments/${payload.id}/movement-schedule`
  );

  return response.data;
};

export interface GetShipmentSupplierByIdRequest {
  id: string;
}

export const getShipmentSupplierById = async (
  payload: GetShipmentSupplierByIdRequest
): Promise<ShipmentSupplierDetailsResponse> => {
  const response = await whitelineApi.get<ShipmentSupplierDetailsResponse>(`/api/shipments/${payload.id}/supplier`);

  return response.data;
};

export interface UpdateShipmentSupplierDataByIdRequest {
  bank_account_number?: string | null;
  bank_address?: string | null;
  bank_beneficiary_name?: string | null;
  bank_cnaps_code?: string | null;
  bank_name?: string | null;
  bank_swift_code?: string | null;
  bank_tel?: string | null;
  contact_full_name?: string | null;
  drop_off_arrival_at?: string | null;
  drop_off_point_id?: string | null;
  drop_off_province_code?: string | null;
  email?: string | null;
  organization_address_en?: string | null;
  organization_name_en?: string | null;
  phone?: string | null;
  shipmentId: string;
  supplier_address_en?: string | null;
  supplier_name_en?: string | null;
  wechat?: string | null;
}

export type UpdateShipmentSupplierDataByIdResponse = ShipmentMovementScheduleResponse;

export const updateShipmentSupplierDataById = async (
  payload: UpdateShipmentSupplierDataByIdRequest
): Promise<UpdateShipmentSupplierDataByIdResponse> => {
  const { shipmentId, ...requestBody } = payload;
  const response = await whitelineApi.patch<UpdateShipmentSupplierDataByIdResponse>(
    `/api/shipments/${shipmentId}/supplier-data`,
    requestBody
  );

  return response.data;
};

export type UpdateShipmentSupplierByIdResponse = ShipmentSupplierDetailsResponse;

export const updateShipmentSupplierById = async (
  payload: UpdateShipmentSupplierByIdRequest
): Promise<UpdateShipmentSupplierByIdResponse> => {
  const { shipmentId, ...requestBody } = payload;
  const response = await whitelineApi.patch<UpdateShipmentSupplierByIdResponse>(
    `/api/shipments/${shipmentId}/supplier`,
    requestBody
  );

  return response.data;
};

export const updateShipmentDetailsById = async (
  payload: UpdateShipmentDetailsByIdRequest
): Promise<UpdateShipmentDetailsByIdResponse> => {
  const {
    id,
    shipmentId,
    ...body
  } = payload;
  const targetShipmentId = id ?? shipmentId;
  const response = await whitelineApi.patch<UpdateShipmentDetailsByIdResponse>(
    `/api/shipments/${targetShipmentId}/details`,
    body
  );

  return response.data;
};

export interface GetShipmentAttachmentsByIdRequest {
  id: string;
}

export type GetShipmentAttachmentsByIdResponse = ShipmentAttachmentListItem[];

export const getShipmentAttachmentsById = async (
  payload: GetShipmentAttachmentsByIdRequest
): Promise<GetShipmentAttachmentsByIdResponse> => {
  const response = await whitelineApi.get<GetShipmentAttachmentsByIdResponse>(
    `/api/shipments/${payload.id}/attachments`
  );

  return response.data;
};

export interface UploadShipmentAttachmentByIdRequest {
  file: File;
  id: string;
  type?: string;
}

export type UploadShipmentAttachmentByIdResponse = ShipmentAttachmentListItem;

export const uploadShipmentAttachmentById = async (
  payload: UploadShipmentAttachmentByIdRequest
): Promise<UploadShipmentAttachmentByIdResponse> => {
  const formData = new FormData();

  formData.append('file', payload.file, payload.file.name);

  if (payload.type) {
    formData.append('type', payload.type);
  }

  const response = await whitelineApi.post<UploadShipmentAttachmentByIdResponse>(
    `/api/shipments/${payload.id}/attachments`,
    formData,
    {
      headers: {
        'Content-Type': undefined
      }
    }
  );

  return response.data;
};

export interface SubmitShipmentPermitsForReviewByIdRequest {
  id: string;
}

export type SubmitShipmentPermitsForReviewByIdResponse = void;

export const submitShipmentPermitsForReviewById = async (
  payload: SubmitShipmentPermitsForReviewByIdRequest
): Promise<SubmitShipmentPermitsForReviewByIdResponse> => {
  const response = await whitelineApi.post<SubmitShipmentPermitsForReviewByIdResponse>(
    `/api/shipments/${payload.id}/permits/submit-for-review`
  );

  return response.data;
};

export interface GetShipmentReceptionResultsRequest {
  shipmentId: string;
}

export type ReceptionCategory = 'DEFECT' | 'OTHER' | 'SHORTAGE' | 'UNKNOWN' | 'WITHDRAWN';

export type ReceptionDecision = 'ACCEPT_AS_IS' | 'DISPOSE' | 'RETURN_TO_SUPPLIER';

export type ShipmentReceptionPositionType = 'DAMAGED' | 'MISSING' | 'SURPLUS' | 'TRANSIT_DAMAGE' | 'UNKNOWN_PRODUCT' | 'WEIGHT_MISMATCH' | 'WITHDRAWN' | 'WRONG_ITEM' | null;

export type ReceptionDecisionStatus = 'PENDING' | 'RESOLVED';

export interface ShipmentReceptionCategory {
  available_decisions: ReceptionDecision[] | null;
  category: ReceptionCategory;
  count: number;
  positions: ShipmentReceptionPosition[] | null;
}

export interface ShipmentReceptionPosition {
  actual_qty: number | null;
  attachments: ShipmentReceptionAttachment[] | null;
  decision: ReceptionDecision | null;
  decision_at: string | null;
  decision_status: ReceptionDecisionStatus;
  defect_qty: number | null;
  note: string | null;
  planned_qty: number | null;
  position_id: string;
  shipment_item_id: string | null;
  title: string | null;
  type: ShipmentReceptionPositionType;
}

export interface GetShipmentReceptionResultsResponse {
  categories: ShipmentReceptionCategory[] | null;
  general_attachments: ShipmentReceptionAttachment[] | null;
  received_at: string | null;
  reception_status: ReceptionStatus;
  shipment_id: string;
  summary: ShipmentReceptionSummary;
}

const getShipmentReceptionResultsEndpoint = (
  shipmentId: string
) => `/api/shipments/${shipmentId}/reception`;

export const getShipmentReceptionResults = async (
  payload: GetShipmentReceptionResultsRequest
): Promise<GetShipmentReceptionResultsResponse> => {
  const endpoint = getShipmentReceptionResultsEndpoint(payload.shipmentId);
  const response = await whitelineApi.get<GetShipmentReceptionResultsResponse>(endpoint);

  return response.data;
};

export interface SubmitShipmentReceptionDecisionsRequest {
  decision: ReceptionDecision;
  position_ids: string[];
  shipmentId: string;
}

export type SubmitShipmentReceptionDecisionsResponse = void;

export const submitShipmentReceptionDecisions = async (
  payload: SubmitShipmentReceptionDecisionsRequest
): Promise<SubmitShipmentReceptionDecisionsResponse> => {
  const response = await whitelineApi.post<SubmitShipmentReceptionDecisionsResponse>(
    `/api/shipments/${payload.shipmentId}/reception/decisions`,
    {
      decision: payload.decision,
      position_ids: payload.position_ids
    }
  );

  return response.data;
};

export interface ConfirmShipmentReceptionByIdRequest {
  shipmentId: string;
}

export interface ConfirmShipmentReceptionByIdResponse {
  accepted_at: string;
}

export const confirmShipmentReceptionById = async (
  payload: ConfirmShipmentReceptionByIdRequest
): Promise<ConfirmShipmentReceptionByIdResponse> => {
  const response = await whitelineApi.post<ConfirmShipmentReceptionByIdResponse>(
    `/api/shipments/${payload.shipmentId}/reception/confirm`
  );

  return response.data;
};

export async function getShipmentItemsApi(
  shipmentId: string
): Promise<ShipmentItemListResponse> {
  const response = await whitelineApi.get<ShipmentItemListResponse>(
    `/api/shipments/${shipmentId}/items`
  );
  return response.data;
}

export async function getShipmentItemApi(
  shipmentId: string,
  itemId: string
): Promise<ShipmentDetailItem> {
  const response = await whitelineApi.get<ShipmentDetailItem>(
    `/api/shipments/${shipmentId}/items/${itemId}`
  );
  return response.data;
}

export interface GetShipmentFinanceScheduleByIdRequest {
  id: string;
}

export type PaymentCategory = 'ADVANCE' | 'CUSTOMS' | 'FINAL' | 'SURCHARGE';

export type FinanceScheduleStatus = 'SCHEDULED' | 'SETTLED';

export interface GetShipmentFinanceScheduleItem {
  amount: number;
  category: PaymentCategory;
  coveredAmount: number;
  date: string;
  dateIsEstimated: boolean;
  description: string | null;
  document: string | null;
  id: string;
  isOverdue: boolean;
  remainingAmount: number;
  status: FinanceScheduleStatus;
  title: string | null;
}

export interface GetShipmentFinanceScheduleByIdResponse {
  currency: string | null;
  items: GetShipmentFinanceScheduleItem[] | null;
}

export const getShipmentFinanceScheduleById = async (
  payload: GetShipmentFinanceScheduleByIdRequest
): Promise<GetShipmentFinanceScheduleByIdResponse> => {
  const response = await whitelineApi.get<GetShipmentFinanceScheduleByIdResponse>(
    `/api/shipments/${payload.id}/finance/schedule`
  );

  return response.data;
};

export const getShipmentCustomsDecisions = async (
  payload: GetShipmentCustomsDecisionsRequest
): Promise<GetShipmentCustomsDecisionsResponse> => {
  const response = await whitelineApi.get<GetShipmentCustomsDecisionsResponse>(
    `/api/shipments/${payload.shipmentId}/customs-decisions`
  );

  return response.data;
};

export const getShipmentCustomsItems = async (
  payload: GetShipmentCustomsItemsRequest
): Promise<GetShipmentCustomsItemsResponse> => {
  const decisions = await getShipmentCustomsDecisions(payload);

  return {
    continued_count: decisions.summary.resubmit_count ?? decisions.resubmit.length,
    customs_status: decisions.summary.customs_status ?? 'NOT_RELEASED',
    items: [
      ...decisions.awaiting,
      ...decisions.resubmit,
      ...decisions.refused
    ],
    pending_count: decisions.summary.awaiting_count ?? decisions.awaiting.length,
    refused_count: decisions.summary.refused_count ?? decisions.refused.length
  };
};

export const submitShipmentCustomsDecision = async (
  payload: SubmitShipmentCustomsDecisionRequest
): Promise<void> => {
  await whitelineApi.post(
    `/api/shipments/${payload.shipmentId}/items/${payload.itemId}/customs-decision`,
    {
      decision: payload.decision
    }
  );
};